import * as React from "react";
import { useNavigate } from "@tanstack/react-router";

const CLEAR_SESSION_PATH = "/api/auth/clear-password";

/**
 * Hook that ends the user session (active staff user) when the tab has been closed.
 *
 * How it works:
 * - The select-user page stores a flag in sessionStorage after successful login.
 * - sessionStorage is tab-scoped: it survives refreshes but is destroyed when the tab is closed.
 * - On mount, if the server reports an active user but sessionStorage has no flag,
 *   we know this is a new tab (the previous tab was closed). We clear the user session
 *   and redirect to select-user.
 * - Pharmacy session is left intact.
 */
export function useUserSessionTimeout(hasActiveUserSession: boolean) {
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!hasActiveUserSession) return;

    const hasTabSession = sessionStorage.getItem("activeUser");
    if (hasTabSession) return; // same tab (refresh) — keep session

    // New tab after previous tab was closed — clear user session
    const clearSession = async () => {
      try {
        await fetch(CLEAR_SESSION_PATH, { method: "POST" });
      } catch {
        // Best effort
      }
      sessionStorage.removeItem("activeUser");
      navigate({ to: "/select-user" });
    };

    clearSession();
  }, [hasActiveUserSession, navigate]);
}
